import { useEffect, useRef, useState } from "react";
import { Search, ChevronDown } from "lucide-react";
import { COUNTRY_CODES, DEFAULT_COUNTRY_DIAL } from "@/lib/countryCodes";

// Splits a combined "+91 9876543210" style value into { dial, number }.
export const splitPhone = (value) => {
    const trimmed = (value || "").trim();
    const match = trimmed.match(/^(\+\d{1,4})\s*(.*)$/);
    if (match) return { dial: match[1], number: match[2] };
    return { dial: DEFAULT_COUNTRY_DIAL, number: trimmed };
};

// Shared phone field: searchable worldwide country-code dropdown + digits-only
// number input, entered right after picking the code. `value` / `onChange`
// behave like a plain text field carrying the combined "+<dial> <digits>"
// string, so callers keep a single `phone` field in state.
const PhoneInput = ({ value, onChange, testId, placeholder = "Phone number" }) => {
    const { dial, number } = splitPhone(value);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const rootRef = useRef(null);
    const searchRef = useRef(null);

    const emit = (nextDial, nextNumber) => onChange(`${nextDial} ${nextNumber}`.trim());

    const selected = COUNTRY_CODES.find((c) => c.dial === dial) || COUNTRY_CODES[0];
    const q = query.trim().toLowerCase();
    const filtered = q
        ? COUNTRY_CODES.filter((c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.iso.toLowerCase() === q)
        : COUNTRY_CODES;

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onEsc);
        searchRef.current?.focus();
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onEsc);
        };
    }, [open]);

    return (
        <div className="mt-1 flex gap-2">
            <div ref={rootRef} className="relative shrink-0">
                <button
                    type="button"
                    data-testid={testId ? `${testId}-country` : undefined}
                    onClick={() => { setOpen((o) => !o); setQuery(""); }}
                    className="flex items-center gap-1.5 h-full rounded-xl border border-[#C266FF]/15 bg-[#12062280] px-3 py-[0.85rem] text-white text-sm hover:border-[#C266FF]/40 transition-colors"
                >
                    <span>{selected.flag}</span>
                    <span className="font-display">{selected.dial}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-[#B0A3C4]" />
                </button>

                {open && (
                    <div className="absolute z-50 mt-2 w-64 max-h-72 overflow-hidden rounded-xl border border-[#C266FF]/25 bg-[#12062F] shadow-[0_18px_44px_-14px_rgba(0,0,0,0.6)] flex flex-col">
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 shrink-0">
                            <Search className="w-4 h-4 text-[#75668C]" />
                            <input
                                ref={searchRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search country or code..."
                                className="bg-transparent text-white text-sm placeholder:text-[#75668C] outline-none flex-1"
                            />
                        </div>
                        <div className="overflow-y-auto">
                            {filtered.length === 0 && (
                                <div className="px-3 py-3 text-sm text-[#75668C]">No matches</div>
                            )}
                            {filtered.map((c) => (
                                <button
                                    key={c.iso}
                                    type="button"
                                    onClick={() => { emit(c.dial, number); setOpen(false); }}
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/[0.06] transition-colors ${c.dial === dial ? "bg-white/[0.06] text-white" : "text-[#B0A3C4]"}`}
                                >
                                    <span>{c.flag}</span>
                                    <span className="flex-1 truncate">{c.name}</span>
                                    <span className="font-display text-[#E9CCFF]">{c.dial}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <input
                type="tel"
                inputMode="numeric"
                data-testid={testId}
                value={number}
                onChange={(e) => emit(dial, e.target.value.replace(/[^0-9]/g, ""))}
                className="sxe-input flex-1 min-w-0"
                placeholder={placeholder}
            />
        </div>
    );
};

export default PhoneInput;
